/**
 * The admin console's decision logic, lifted out of the components.
 *
 * WHY THIS FILE EXISTS. Every rule in here is one the screens must get RIGHT
 * rather than merely render: refusing a cell-level untick of a wildcard rule,
 * sending only the fields that actually changed, clearing the cursor whenever a
 * filter moves, and reading "zero" back to the operator as a posture rather than
 * as "unlimited". Each of those is a money or accountability decision wearing a
 * checkbox, and while it lives inside a component the only way to exercise it is
 * to render a DOM — which this repo has no harness for and which would test the
 * markup at the same time as the rule.
 *
 * So the rules are pure functions here, and the components become the thin
 * layer that calls them. Nothing in this file imports React, touches the
 * network, or reads a clock: every time-dependent function takes `now` as an
 * argument, because an ops screen's only honest clock is the timestamp of the
 * fetch it is describing, never `Date.now()` at render.
 */
import { formatCents } from '@/utils/money';
import type { AdminParams } from '@/types/api';
import type { AdminUsersQuery } from '@/api/adminEndpoints';

/* ------------------------------------------------------------------ *
 * Market visibility
 * ------------------------------------------------------------------ */

/**
 * A market the catalogue has not reported for this long is shown greyed rather
 * than dropped — a seasonal market returning to the board is one of the most
 * common reasons to open that screen at all.
 */
export const STALE_MS = 24 * 60 * 60 * 1000;

/** The subset of a visibility cell that any of these rules actually reads. */
export interface VisibilityCell {
  sportKey: string;
  marketKey: string;
  hidden: boolean;
  /** The rule suppressing this cell — may be a wildcard like `soccer:*`. */
  hiddenBy: string | null;
  lastSeenAt: string;
}

/** Pending edits, keyed `sport:market`. Absent = no change from server state. */
export type PendingVisibility = Record<string, boolean>;

export function cellKey(cell: Pick<VisibilityCell, 'sportKey' | 'marketKey'>): string {
  return `${cell.sportKey}:${cell.marketKey}`;
}

/**
 * True when this cell is hidden by a rule that is NOT the cell's own key — i.e.
 * a wildcard. Unticking it would silently re-open every sibling the wildcard
 * covers, so the checkbox is disabled and {@link applyVisibilityToggle} refuses.
 */
export function isWildcardHidden(cell: VisibilityCell): boolean {
  return cell.hidden && cell.hiddenBy !== null && cell.hiddenBy !== cellKey(cell);
}

/** Age is measured against the FETCH timestamp, never a render-time clock. */
export function isStale(lastSeenAt: string, now: number, staleMs: number = STALE_MS): boolean {
  return now - new Date(lastSeenAt).getTime() > staleMs;
}

/**
 * Whether a stored rule matches anything the catalogue currently reports. A rule
 * that matches nothing has no checkbox, which is exactly why the rules render as
 * their own list above the grid — otherwise it would be invisible AND
 * unremovable, and a market would stay suppressed by something unreachable.
 */
export function ruleMatchesLiveCell(rule: string, cells: readonly VisibilityCell[]): boolean {
  return cells.some((c) => c.hiddenBy === rule || cellKey(c) === rule);
}

/**
 * The next hidden set: the CURRENT stored rules plus the pending ticks. Derived
 * rather than held in state so a background refetch of the params row cannot
 * leave a draft describing a rule set that no longer exists.
 */
export function deriveNextHidden(
  storedHidden: readonly string[],
  pending: PendingVisibility,
): string[] {
  const next = new Set(storedHidden);
  for (const [key, hide] of Object.entries(pending)) {
    if (hide) next.add(key);
    else next.delete(key);
  }
  return [...next].sort();
}

/**
 * Apply one checkbox click. Returns the PREVIOUS object unchanged when the
 * toggle is refused or is a no-op, so React skips the re-render.
 *
 * Two refusals are encoded here, and they are different things:
 *  - a wildcard untick is REFUSED outright rather than guessed at;
 *  - a tick that lands back on the stored value DELETES the pending entry, so
 *    "3 changes" never counts an edit the operator has already undone.
 */
export function applyVisibilityToggle(
  pending: PendingVisibility,
  args: {
    key: string;
    currentlyHidden: boolean;
    hiddenBy: string | null;
    storedHidden: readonly string[];
  },
): PendingVisibility {
  const { key, currentlyHidden, hiddenBy, storedHidden } = args;
  if (currentlyHidden && hiddenBy && hiddenBy !== key) return pending;

  const target = !currentlyHidden;
  const storedIsHidden = storedHidden.includes(key);
  const next = { ...pending };
  if (target === storedIsHidden) delete next[key];
  else next[key] = target;
  return next;
}

/* ------------------------------------------------------------------ *
 * Risk limits (params)
 * ------------------------------------------------------------------ */

type NumericKey = {
  [K in keyof AdminParams]-?: AdminParams[K] extends number | undefined ? K : never;
}[keyof AdminParams];

export type Unit = 'cents' | 'bps' | 'ms' | 'count';

export interface FieldSpec {
  key: Exclude<NumericKey, 'version'>;
  label: string;
  unit: Unit;
  /** What zero actually does. Null when zero is not a meaningful setting here. */
  zeroMeans: string | null;
  min: number;
  max?: number;
  help: string;
}

export interface Group {
  title: string;
  blurb: string;
  fields: FieldSpec[];
}

const REFUSE_ALL =
  'Refuses every delayed-price bet in this scope. This is the emergency lever, not “unlimited”.';

/**
 * EVERY `max` HERE MIRRORS A DATABASE CHECK. A limit an operator can save above
 * the constraint it sits under is not a limit — it is a 500 waiting for the save
 * button, presenting as a broken console rather than as a refused decision.
 */
const REFUSE_ALL_BETS =
  'Refuses every new real-money bet, delayed-price or not. This is the book-wide emergency lever, not “unlimited”.';

export const GROUPS: Group[] = [
  {
    title: 'Stake cap — every real-money bet (C-6)',
    blurb:
      'The one cap that applies to every new placement regardless of price source. A bet may stake at most this share of the customer’s Play Balance, bounded by the absolute ceiling — whichever is smaller. Savings never contributes. The delayed-price limits below still apply on top; raising the ceiling here does not raise them.',
    fields: [
      {
        key: 'maxStakeBalanceBps',
        label: 'Share of Play Balance',
        unit: 'bps',
        zeroMeans: REFUSE_ALL_BETS,
        min: 0,
        max: 10_000,
        help: 'Basis points of the customer’s current Play Balance. 2500 = 25%. The share binds below a balance where it falls under the ceiling; the $1 minimum stake still applies when both settings are positive.',
      },
      {
        key: 'maxStakeCeilingCents',
        label: 'Absolute ceiling per bet',
        unit: 'cents',
        zeroMeans: REFUSE_ALL_BETS,
        min: 0,
        max: 100_000_000,
        help: 'The most any customer may stake on one bet, however large their balance.',
      },
    ],
  },
  {
    title: 'Delayed prices — one bet',
    blurb:
      'Our Betfair key returns delayed data, so a delayed price is stakeable by design. These two bound what a single bet on a stale price can cost. Neither subsumes the other: $30 at odds of 50 is $1,500 of liability, and a payout cap alone still permits churning large stakes at short prices.',
    fields: [
      {
        key: 'delayedPriceMaxStakeCents',
        label: 'Max stake per bet',
        unit: 'cents',
        zeroMeans: REFUSE_ALL,
        min: 0,
        max: 100_000_000,
        help: 'The most one customer may stake on a single delayed-price selection.',
      },
      {
        key: 'delayedPriceMaxPayoutCents',
        label: 'Max liability per bet',
        unit: 'cents',
        zeroMeans: REFUSE_ALL,
        min: 0,
        max: 100_000_000_000,
        help: 'Stake × odds. This is what an arbitrageur actually extracts, which is why it is capped separately from stake.',
      },
    ],
  },
  {
    title: 'Delayed prices — one stored quote',
    blurb:
      'How much may be struck against a single stored price before it refreshes. The allowance is the smaller of this ceiling and the exchange depth × the liquidity share, and a quote carrying no depth is refused outright rather than assumed.',
    fields: [
      {
        key: 'delayedPriceMaxQuoteStakeCents',
        label: 'Operator ceiling per quote',
        unit: 'cents',
        zeroMeans: REFUSE_ALL,
        min: 0,
        help: 'Codex recommends $25. Note this sits below the stakes some tests use — raising it is a risk decision, not a fix.',
      },
      {
        key: 'delayedPriceQuoteLiquidityBps',
        label: 'Share of exchange depth',
        unit: 'bps',
        zeroMeans: REFUSE_ALL,
        min: 0,
        max: 10_000,
        help: 'Capped at 10000 (100%). A share above 100% would authorise staking more than the exchange ever reported — capacity invented in config.',
      },
      {
        key: 'delayedPriceMaxPlacementAgeMs',
        label: 'Max DELAYED price age at placement',
        unit: 'ms',
        // Minimum 1, because zero here refuses by arithmetic rather than by
        // decision and reads as an outage.
        zeroMeans: null,
        min: 1,
        max: 1_200_000,
        help: 'For prices the provider flags as delayed (racing on the delayed key). Separate from the 20-minute display TTL: that says how long a price is SHOWN, this says how long it may be STAKED. Minimum 1 — zero here would look like an outage, not a posture.',
      },
      {
        key: 'priceMaxPlacementAgeMs',
        label: 'Max price age at placement (not delayed)',
        unit: 'ms',
        zeroMeans: null,
        min: 1,
        max: 1_200_000,
        help: 'For ordinary bookmaker prices (sports from The Odds API), which are only re-observed on the 15-minute catalogue sync. Default 960000 (16 min). A price older than the delayed-price age above is still placeable but takes the delayed-price stake and payout caps. Must not exceed the quote retention TTL.',
      },
    ],
  },
  {
    title: 'Delayed prices — aggregate exposure',
    blurb:
      'Per-bet caps are a blast-radius guard, not a bound: fifty bets at the cap cost what one huge bet would. These four scopes each answer a question the one below it is structurally blind to — a customer spreading across forty quotes breaches no quote allowance; an arbitrageur working thirty markets breaches no per-market ceiling.',
    fields: [
      { key: 'delayedPriceMaxCustomerStakeCents', label: 'Customer — open stake', unit: 'cents', zeroMeans: REFUSE_ALL, min: 0, help: 'Across every open delayed-price bet this customer holds.' },
      { key: 'delayedPriceMaxCustomerPayoutCents', label: 'Customer — open liability', unit: 'cents', zeroMeans: REFUSE_ALL, min: 0, help: 'The same set, measured in what we could owe.' },
      { key: 'delayedPriceMaxMarketStakeCents', label: 'Market — open stake', unit: 'cents', zeroMeans: REFUSE_ALL, min: 0, help: 'Catches colluding accounts each individually inside the customer ceiling.' },
      { key: 'delayedPriceMaxMarketPayoutCents', label: 'Market — open liability', unit: 'cents', zeroMeans: REFUSE_ALL, min: 0, help: 'One position on one market, however many accounts composed it.' },
      { key: 'delayedPriceMaxBookStakeCents', label: 'Book — open stake', unit: 'cents', zeroMeans: REFUSE_ALL, min: 0, help: 'The strongest lever here: zero closes delayed-price betting book-wide, which with our delayed key means all AU racing.' },
      { key: 'delayedPriceMaxBookPayoutCents', label: 'Book — open liability', unit: 'cents', zeroMeans: REFUSE_ALL, min: 0, help: 'Total delayed-price liability we are carrying at once.' },
    ],
  },
  {
    title: 'Commercial',
    blurb: 'Revenue and deposit settings. These are not risk limits and are not part of the delayed-price emergency lever.',
    fields: [
      { key: 'transactionFeeBps', label: 'Transaction fee', unit: 'bps', zeroMeans: 'No fee is charged on any bet.', min: 0, max: 2000, help: '400 = 4%, charged on top of the stake at placement and never refunded.' },
      { key: 'minDepositCents', label: 'Minimum deposit', unit: 'cents', zeroMeans: 'No minimum.', min: 0, help: '' },
      { key: 'recommendedSavingsSplitBps', label: 'Recommended savings split', unit: 'bps', zeroMeans: 'Nothing is routed to Savings by default.', min: 0, max: 10_000, help: 'The share of a first deposit that lands in locked Savings.' },
      { key: 'signupMatchCreditCapCents', label: 'Sign-up legacy credit cap', unit: 'cents', zeroMeans: 'No legacy credit is issued.', min: 0, help: 'Legacy — this credit was removed in v0.11. Kept for historical rows.' },
    ],
  },
  {
    title: 'Referrals',
    blurb: 'The referral programme. `referralActive` gates the whole thing and is edited below the numbers.',
    fields: [
      { key: 'referralRewardMatchCreditCents', label: 'Reward per conversion', unit: 'cents', zeroMeans: 'Conversions are recorded but pay nothing.', min: 0, help: '' },
      { key: 'referralMaxPerReferrer', label: 'Max rewards per referrer', unit: 'count', zeroMeans: 'No referrer can earn a reward.', min: 0, help: '' },
      { key: 'referralMinRefereeDepositCents', label: 'Minimum referee deposit', unit: 'cents', zeroMeans: 'Any deposit qualifies.', min: 0, help: '' },
    ],
  },
];

export const ALL_FIELDS: FieldSpec[] = GROUPS.flatMap((g) => g.fields);

/**
 * The plain-English reading of a value, shown live beneath its input. Zero takes
 * priority over the unit formatting deliberately: `$0.00` is what an operator
 * skims past, and "Refuses every delayed-price bet in this scope" is not.
 */
export function readBack(value: number, spec: FieldSpec): string {
  if (value === 0 && spec.zeroMeans) return spec.zeroMeans;
  switch (spec.unit) {
    case 'cents':
      return formatCents(value);
    case 'bps':
      return `${(value / 100).toFixed(2)}%`;
    case 'ms':
      return value >= 1000
        ? `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)} seconds`
        : `${value} ms`;
    case 'count':
      return `${value}`;
  }
}

export function unitSuffix(unit: Unit): string {
  return unit === 'cents' ? 'cents' : unit === 'bps' ? 'bps' : unit === 'ms' ? 'ms' : '';
}

export type ParamsDraft = Record<string, string>;

/**
 * Which fields actually differ from the loaded row. A blank input is NOT a
 * change — it is an in-progress edit, and reporting it as a change would let a
 * half-typed field reach the save payload as `NaN`.
 */
export function changedFields(
  draft: ParamsDraft,
  referralActive: boolean,
  data: AdminParams,
): string[] {
  const out: string[] = [];
  for (const f of ALL_FIELDS) {
    const raw = draft[f.key];
    if (raw === undefined) continue;
    if (raw.trim() === '') continue;
    if (Number(raw) !== (data[f.key] ?? 0)) out.push(f.key);
  }
  if (referralActive !== data.referralActive) out.push('referralActive');
  return out;
}

/** Field key → the message shown in place of the read-back line. */
export function validateDraft(draft: ParamsDraft): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of ALL_FIELDS) {
    const raw = draft[f.key];
    if (raw === undefined) continue;
    if (raw.trim() === '') {
      out[f.key] = 'Required';
      continue;
    }
    const n = Number(raw);
    if (!Number.isInteger(n)) out[f.key] = 'Whole numbers only';
    else if (n < f.min)
      out[f.key] =
        f.min === 1
          ? 'Minimum is 1 — zero here reads as an outage, not a posture'
          : `Minimum is ${f.min}`;
    else if (f.max !== undefined && n > f.max)
      out[f.key] = `Maximum is ${f.max} — the database CHECK refuses anything above it`;
  }
  return out;
}

export type ParamsSavePayload = Partial<AdminParams> & {
  expectedVersion: number;
  note?: string;
};

/**
 * ONLY CHANGED FIELDS GO OUT. The server records `changedFields` on the audit
 * row, so posting the whole form would attribute every untouched limit to
 * whoever last pressed save — an audit trail naming the wrong author is worse
 * than none. `expectedVersion` is the one always-present field: it is the write
 * token the 409 guard compares.
 */
export function buildSavePayload(args: {
  data: AdminParams;
  draft: ParamsDraft;
  changed: readonly string[];
  referralActive: boolean;
  note: string;
}): ParamsSavePayload {
  const { data, draft, changed, referralActive, note } = args;
  const body: ParamsSavePayload = { expectedVersion: data.version };
  /* The keys are only known at runtime; the alias keeps the cast local. */
  const numeric = body as unknown as Record<string, number>;
  for (const key of changed) {
    if (key === 'referralActive') body.referralActive = referralActive;
    else numeric[key] = Number(draft[key]);
  }
  if (note.trim()) body.note = note.trim();
  return body;
}

/* ------------------------------------------------------------------ *
 * Users list — cursor + filters
 * ------------------------------------------------------------------ */

export const PAGE_SIZE = 25;

/** The filter keys a user can set; `limit` and `cursor` are not filters. */
export const FILTER_KEYS = ['q', 'role', 'kycStatus', 'status'] as const;

/**
 * A cursor is a POSITION IN ONE ORDERING, so every filter change drops it.
 * Carrying it across asks the server to resume a list that no longer exists,
 * and the page that comes back is a slice of neither ordering.
 *
 * Re-selecting the active value CLEARS that filter — the chips are toggles.
 */
export function applyFilter<K extends keyof AdminUsersQuery>(
  prev: AdminUsersQuery,
  key: K,
  value: AdminUsersQuery[K],
): AdminUsersQuery {
  const next: AdminUsersQuery = { ...prev, limit: PAGE_SIZE };
  delete next.cursor;
  if (prev[key] === value) delete next[key];
  else next[key] = value;
  return next;
}

/** Same cursor rule. A blank search REMOVES `q` rather than sending an empty one. */
export function applySearch(prev: AdminUsersQuery, search: string): AdminUsersQuery {
  const next: AdminUsersQuery = { ...prev, limit: PAGE_SIZE };
  delete next.cursor;
  if (search.trim()) next.q = search.trim();
  else delete next.q;
  return next;
}

export function countActiveFilters(filters: AdminUsersQuery): number {
  return FILTER_KEYS.filter((k) => filters[k] !== undefined).length;
}

/* ------------------------------------------------------------------ *
 * Operator actions — the reason, and the restatement
 * ------------------------------------------------------------------ */

/**
 * THE REASON IS THE AUDIT RECORD, not a courtesy field.
 *
 * Every mutation on these screens writes an `admin_audit_log` row naming the
 * operator, and six weeks later that row is the only account of WHY a limit
 * moved or why someone's savings were released. A blank or one-word reason
 * makes a deliberate decision indistinguishable from a mis-click, which is the
 * single property the audit log exists to provide. So the floor is enforced
 * here, before the request is composed, rather than left to the server to
 * refuse after the operator has already pressed the button.
 */
export const REASON_MIN = 3;
export const REASON_MAX = 500;

/** Returns the message to show, or null when the reason is acceptable. */
export function validateReason(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'A reason is required — it is the audit record';
  if (trimmed.length < REASON_MIN) return `At least ${REASON_MIN} characters`;
  if (trimmed.length > REASON_MAX) return `At most ${REASON_MAX} characters`;
  return null;
}

/**
 * A money amount typed by an operator, in DOLLARS, converted to integer cents.
 *
 * Returns `null` for anything that is not a clean amount, so callers branch on
 * the failure rather than on a NaN that would post as `null` and read
 * server-side as "no amount given" — which on the unlock endpoint means
 * UNLOCK EVERYTHING. A silent coercion here is the worst failure in the file.
 */
export function parseDollarsToCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  return Number.isSafeInteger(cents) ? cents : null;
}

/**
 * The signed legacy-credit adjustment.
 *
 * ZERO IS REJECTED, matching the server's `Non-zero` contract: an adjustment of
 * nothing writes an audit row claiming a correction that moved no money.
 */
export function validateAdjustmentAmount(raw: string): string | null {
  if (raw.trim() === '') return 'Required';
  const cents = parseDollarsToCents(raw);
  if (cents === null) return 'Enter an amount in dollars, e.g. -25.00';
  if (cents === 0) return 'Zero moves nothing — enter a positive or negative amount';
  return null;
}

/**
 * The confirmation sentence for a signed adjustment, RESTATED IN WORDS.
 *
 * `-2500` on a form reads as a number; "Remove $25.00 from…" reads as an
 * instruction, and the direction is the half an operator gets wrong. The sign
 * is never shown on its own in the confirm step.
 */
export function describeAdjustment(cents: number, email: string): string {
  const verb = cents < 0 ? 'Remove' : 'Add';
  const preposition = cents < 0 ? 'from' : 'to';
  return `${verb} ${formatCents(Math.abs(cents))} ${preposition} ${email}'s legacy credit balance.`;
}

/**
 * The savings break-out amount.
 *
 * A BLANK BOX IS A LEGITIMATE INSTRUCTION here and means "release everything" —
 * the endpoint reads a null `amountCents` that way. So blank is NOT an error,
 * and the field is never pre-filled with the balance: a pre-filled default is
 * indistinguishable from a deliberate full release that the operator typed.
 */
export function validateUnlockAmount(raw: string, savingsCents: number): string | null {
  if (raw.trim() === '') return null;
  const cents = parseDollarsToCents(raw);
  if (cents === null) return 'Enter an amount in dollars, e.g. 50.00';
  if (cents <= 0) return 'Must be more than zero';
  if (cents > savingsCents)
    return `More than the ${formatCents(savingsCents)} currently locked`;
  return null;
}

/** The confirmation sentence for a break-out, full or partial. */
export function describeUnlock(
  amountCents: number | null,
  savingsCents: number,
  email: string,
): string {
  return amountCents === null
    ? `Release ALL ${formatCents(savingsCents)} of locked Savings to ${email}'s Play Balance.`
    : `Release ${formatCents(amountCents)} of ${formatCents(savingsCents)} locked Savings to ${email}'s Play Balance.`;
}

/*
  ---------------------------------------------------------------------------
  Resolving a HELD referral attribution
  ---------------------------------------------------------------------------

  The §16.5 fraud guards park a suspicious attribution in `status='held'` rather
  than paying or refusing it. Compliance reviews it offline; this is where the
  answer comes back.

  ONLY A HELD ROW IS RESOLVABLE. Every other status is already an answer — the
  server returns 409 `not_held` for them, which is a distinct outcome from a
  failure and is presented as one. The control is therefore rendered only on
  held rows, so an operator never composes a reason for a decision that was
  taken before they opened the page.
*/
export const REFERRAL_RESOLUTIONS = ['converted', 'rejected'] as const;
export type ReferralResolution = (typeof REFERRAL_RESOLUTIONS)[number];

/** Only a held attribution can be resolved; every other status IS the answer. */
export function isResolvableAttribution(status: string): boolean {
  return status === 'held';
}

/**
 * The resolution, restated as the MONEY DECISION it is.
 *
 * The two words on the wire — `converted` and `rejected` — say nothing about
 * what happens to anyone's balance, and the half that matters is that
 * converting EMITS A REWARD LEDGER EVENT to the referrer while rejecting emits
 * nothing. So the sentence names the emission rather than the status flip.
 *
 * The AMOUNT is deliberately NOT stated. The server pays the live
 * `referral_reward_match_credit_cents` at the moment of the write, and this
 * screen does not read that param — quoting a number from anywhere else would
 * be a promise the console cannot keep, and an operator who checked it against
 * the resulting ledger event would find them disagreeing.
 *
 * `side` is the side THIS page's account sits on, so the sentence can say who
 * is paid without the operator having to work it out from two ids.
 */
export function describeReferralResolution(
  resolution: ReferralResolution,
  side: 'referrer' | 'referee',
  otherName: string | null | undefined,
): string {
  const other = otherName && otherName.trim() !== '' ? otherName.trim() : 'the other account';
  const referrer = side === 'referrer' ? 'this account' : other;
  if (resolution === 'converted') {
    return `Treat this attribution as genuine: the referrer (${referrer}) is paid the current referral reward as legacy credit, and the attribution is marked converted.`;
  }
  return `Treat this attribution as fraudulent: no reward is paid to the referrer (${referrer}), and the attribution is marked rejected.`;
}

/*
  ---------------------------------------------------------------------------
  Role sets — MIRRORED from api/src/lib/admin-role.ts, and display-only
  ---------------------------------------------------------------------------

  These decide which buttons an operator is offered. They decide NOTHING about
  what the server will do: every /admin route re-checks the JWT claim and
  returns 403 whatever this concluded. Hiding a control the server would refuse
  saves an operator from composing a reason for an action they cannot take; it
  is not a security boundary and must never be described as one.

  THERE IS NO IMPLICIT HIERARCHY. `owner` does not auto-imply `admin` — each set
  lists its members literally, exactly as the API's sets do. A hierarchy
  invented here would diverge from the server's the first time a role is added,
  and it would diverge in the permissive direction.

  Where the handoff and the OpenAPI contract disagree on a gate, the STRICTER
  reading wins here: offering a button the server refuses teaches operators that
  403s are normal, which is how a real one gets ignored.
*/
export type StaffRole = 'owner' | 'admin' | 'compliance' | 'support' | 'readonly';

export const ROLE_SETS = {
  ANY_ADMIN: ['owner', 'admin', 'compliance', 'support', 'readonly'],
  WRITE: ['owner', 'admin'],
  COMPLIANCE_OR_HIGHER: ['owner', 'admin', 'compliance'],
  OWNER_ONLY: ['owner'],
} as const satisfies Record<string, readonly StaffRole[]>;

/** `null` is an ordinary customer — a value, not an absence. Never authorised. */
export function isRoleAuthorized(
  role: string | null | undefined,
  allow: readonly string[],
): boolean {
  if (role === null || role === undefined) return false;
  return allow.includes(role);
}

/** Roles an operator can be moved to. `null` (demote to customer) is separate. */
export const ASSIGNABLE_ROLES: readonly StaffRole[] = [
  'owner',
  'admin',
  'compliance',
  'support',
  'readonly',
];

export const KYC_STATUSES = ['unverified', 'pending', 'verified', 'rejected'] as const;
export type KycStatus = (typeof KYC_STATUSES)[number];

export const ACCOUNT_STATUSES = ['active', 'suspended', 'closed'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/**
 * "Nothing to change" is caught HERE, not by the server's 400 `no_change`.
 *
 * The server refusing an unchanged write is correct and must stay, but arriving
 * at it costs the operator a written reason and an error panel for what was
 * actually a mis-click on the value already selected. Reported as a field
 * error, so the confirm button is blocked rather than merely apologetic.
 */
export function validateChanged<T>(next: T, current: T, noun: string): string | null {
  return next === current ? `Already ${noun} — nothing would change` : null;
}

/** The role change, restated as an instruction rather than a pair of tokens. */
export function describeRoleChange(
  next: StaffRole | null,
  previous: StaffRole | null,
  email: string,
): string {
  const from = previous ?? 'ordinary customer';
  if (next === null) {
    return `Remove ${email}'s staff role. They are currently ${from} and will become an ordinary customer with no console access.`;
  }
  return `Make ${email} a ${next}. They are currently ${from}.`;
}

/**
 * The KYC override.
 *
 * Named as an OVERRIDE rather than as a status change, because that is what it
 * is: a staff assertion, recorded against the operator's own account, that
 * identity was established outside the automated flow.
 */
export function describeKycChange(next: KycStatus, previous: string, email: string): string {
  return `Override ${email}'s KYC status to ${next}, from ${previous}. This is recorded as your assertion, not the provider's.`;
}

/**
 * The status change.
 *
 * Suspension and closure REVOKE ACTIVE SESSIONS, which is the consequence an
 * operator most often does not expect — so it is stated before the action, not
 * discovered afterwards in the `revokedSessions` count that comes back.
 */
export function describeStatusChange(
  next: AccountStatus,
  previous: string,
  email: string,
): string {
  const base = `Set ${email}'s account to ${next}, from ${previous}.`;
  if (next === 'active') return `${base} They will be able to sign in and bet again.`;
  return `${base} They will be signed out everywhere and cannot sign in again until this is reversed.`;
}

/*
  ---------------------------------------------------------------------------
  Re-settlement — the correction queue
  ---------------------------------------------------------------------------

  A proposal is written by the bet-settlement job when provider consensus
  contradicts a settlement the ledger already recorded. Writing it MOVES NO
  MONEY. Applying it moves money in BOTH DIRECTIONS in one transaction — the
  recorded settlement is reversed out of the ledger and a replacement applied —
  which is why the apply path is OWNER_ONLY and why rejecting is too: "the
  provider is wrong and the original stands" is an equally accountable ruling,
  not a queue-clearing click.

  THE OPERATOR TYPES NOTHING BUT THE REASON. Outcome, factor, request id and
  `expectedSettlementVersion` are all CARRIED from the row that was clicked.
  The version especially: it is the optimistic-concurrency guard, and a
  hand-typed one defeats the only thing standing between an operator and a
  second correction stacked on top of a first they never saw.
*/

/** The subset of a proposal row these rules read. */
export interface ResettlementProposal {
  betId: string;
  currentStatus: string;
  currentSettlementVersion: number;
  proposedOutcome: string;
  proposedFactor: number;
  proposedBy: string;
  reason: string;
  status: string;
}

/** Only a pending proposal can be acted on; every other status IS the answer. */
export function isPendingProposal(status: string): boolean {
  return status === 'pending';
}

/**
 * Settlement factors are NUMERIC(6,4) and both the proposing job and the apply
 * path compare them at four decimal places. Comparing them any other way here
 * would let the console call a dead-heat correction "no change" while the
 * server disagrees, or the reverse.
 */
export function sameFactor(a: number, b: number): boolean {
  return Math.round(a * 10000) === Math.round(b * 10000);
}

/**
 * A factor is only worth showing when it is not a whole win or a whole loss.
 * `1` and `0` restate the outcome word and add nothing; a dead heat at `0.5`
 * is the entire content of the correction.
 */
export function formatFactor(factor: number): string | null {
  if (sameFactor(factor, 1) || sameFactor(factor, 0)) return null;
  return String(Number(factor.toFixed(4)));
}

/**
 * The correction, restated as the LEDGER MOVEMENT it is.
 *
 * Three things the two outcome words do not say, and all three are the half an
 * operator gets wrong:
 *
 * 1. The recorded settlement is REVERSED OUT OF THE LEDGER first. This is not
 *    an edit of a status column; compensating events are written.
 * 2. The PLACEMENT DEBIT AND THE 4% FEE ARE NOT REVERSED. The stake was spent
 *    and the fee was charged when the bet was struck, and neither is undone by
 *    correcting the result. An operator who assumes otherwise will tell a
 *    customer they are getting their fee back.
 * 3. A DEAD-HEAT correction can carry the SAME outcome word on both sides and
 *    differ only in the factor — so when the words match, the sentence leads
 *    with the factor instead, or it would read as a change to nothing.
 *
 * No version number appears in the sentence. The version is carried, not
 * decided, and quoting it would invite an operator to check it rather than to
 * check the decision.
 */
export function describeResettlement(p: ResettlementProposal): string {
  const factor = formatFactor(p.proposedFactor);
  const outcomeUnchanged = p.proposedOutcome === p.currentStatus;

  const head = outcomeUnchanged
    ? `Re-settle this bet as ${p.proposedOutcome} at a settlement factor of ${factor ?? '1'}, replacing the factor it was settled at.`
    : `Re-settle this bet as ${p.proposedOutcome}, replacing the recorded ${p.currentStatus}${
        factor ? ` at a settlement factor of ${factor}` : ''
      }.`;

  return `${head} The recorded settlement is reversed out of the ledger and the replacement applied in one transaction. The placement stake and the 4% fee are NOT reversed.`;
}

/** The rejection, restated as the ruling it is rather than as a status flip. */
export function describeResettlementRejection(p: ResettlementProposal): string {
  return `Reject this correction: the recorded ${p.currentStatus} STANDS, no money moves, and the proposal is closed. Recorded against your account as your ruling that the provider result is wrong.`;
}

/**
 * Settling a bet that has never settled. Distinct from a correction in the one
 * way that matters — nothing is being reversed, because nothing has moved yet.
 */
export function describeSettleBet(betId: string, outcome: string): string {
  const short = betId.slice(0, 8);
  if (outcome === 'void') {
    return `Void bet ${short} — the stake is returned. The 4% placement fee is NOT returned.`;
  }
  return `Settle bet ${short} as ${outcome}. Nothing is being reversed: this bet has not settled yet.`;
}

/*
  ---------------------------------------------------------------------------
  Fixture quarantines
  ---------------------------------------------------------------------------

  A quarantine STRUCTURALLY WITHHOLDS a fixture: its markets are suspended, it
  is dropped from `/markets`, and placement refuses it. Three states exist in
  the table and only two of them are ever returned — `active` and
  `pending_refresh` — because a released row is filtered out by the API.

  THE LIFECYCLE IS THE WHOLE POINT OF THIS SCREEN, and the half operators get
  wrong is the second transition. Clearing a quarantine does NOT reopen the
  fixture. It moves the row to `pending_refresh`, and the fixture stays
  unplaceable until ITS OWN catalogue transaction rewrites every market and
  quote and sets `released_at` in that same transaction. A successful
  neighbouring fixture never certifies it. So there is no operator action that
  reopens a fixture — there is only an operator action that lets the catalogue
  earn it back.

  `pending_refresh` appears on NO OTHER SURFACE in the console. An operator who
  reads "cleared" as "reopened" will tell someone the race is back on the board
  when it is not, so every sentence below is written to make that impossible.
*/

/** What the API's `state` field can be. Released rows are never returned. */
export type QuarantineState = 'active' | 'pending_refresh';

/**
 * Mirrors the API's `FixtureId = z.string().regex(/^[0-9a-f]{16}$/)` exactly.
 *
 * Checked here rather than left to the server's 400 because the fixture id is
 * the ONE field on this screen an operator types by hand — it is copied out of
 * a log line or another console — and a transposed character is far more likely
 * than a malformed one. A 400 that says "validation_error" after a written
 * reason costs the reason.
 */
export const FIXTURE_ID_RE = /^[0-9a-f]{16}$/;

export function validateFixtureId(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'A fixture id is required';
  if (!FIXTURE_ID_RE.test(trimmed)) return 'Must be exactly 16 lowercase hex characters';
  return null;
}

/**
 * Applying a quarantine, restated as the withholding it is.
 *
 * `affectedMarkets` is deliberately NOT predicted here. The count comes back
 * from the server after `suspendFixtureMarkets` runs, and a number guessed
 * beforehand from a list read minutes ago would be a claim about what is about
 * to be suspended rather than a report of what was.
 */
export function describeQuarantineApply(fixtureId: string, eventName?: string | null): string {
  const what = eventName ? `${eventName} (${fixtureId})` : fixtureId;
  return `Withhold ${what} from the board. Its markets are suspended immediately, it stops appearing in /markets, and placement refuses it. Bets already struck on it are not affected.`;
}

/**
 * Clearing a quarantine.
 *
 * THIS SENTENCE MUST NEVER SAY "REOPEN", and the test file pins that. The clear
 * is a request; the catalogue is what grants it. Stating anything stronger
 * would have an operator announce a fixture is back on the board at the moment
 * it is guaranteed still not to be.
 */
export function describeQuarantineClear(fixtureId: string, eventName?: string | null): string {
  const what = eventName ? `${eventName} (${fixtureId})` : fixtureId;
  return `Ask for the quarantine on ${what} to be cleared. This does NOT put the fixture back on the board: its markets stay suspended and placement keeps refusing it until that fixture's own catalogue sync rewrites it successfully. Your reason is recorded as the audit record for that decision.`;
}

/**
 * What an operator can do to a row, derived from its state alone.
 *
 * Offered as a state-derived answer rather than as two independent booleans
 * because the two actions are mutually exclusive by construction: the API
 * returns 409 `fixture_already_quarantined` for a re-apply on `active`, and 409
 * `fixture_quarantine_clear_pending` for a second clear on `pending_refresh`.
 * Deriving both from one value is what stops the console offering a button the
 * server is certain to refuse.
 */
export function quarantineActions(state: QuarantineState): {
  canClear: boolean;
  canReapply: boolean;
} {
  return { canClear: state === 'active', canReapply: state === 'pending_refresh' };
}

/**
 * The state, in the words an operator needs rather than the token.
 *
 * `pending_refresh` gets the longer sentence on purpose: `active` means what it
 * looks like, and `pending_refresh` does not.
 */
export function describeQuarantineState(state: QuarantineState): string {
  return state === 'active'
    ? 'Withheld — markets suspended, placement refused'
    : 'Clear requested — STILL withheld until its own catalogue sync rewrites it';
}

/**
 * Pending bets on a withheld fixture are the number that decides urgency.
 *
 * Zero is reported as a sentence rather than omitted: a withheld fixture with
 * nothing staked on it is the system working, and leaving the line out would
 * make that indistinguishable from a count nobody computed.
 */
export function describeQuarantineExposure(pendingBets: number, affectedMarkets: number): string {
  const markets = `${affectedMarkets} market${affectedMarkets === 1 ? '' : 's'} suspended`;
  if (pendingBets === 0) return `${markets}, no bets pending — nothing is staked on this fixture.`;
  return `${markets}, ${pendingBets} bet${pendingBets === 1 ? '' : 's'} PENDING — these cannot settle while the fixture is withheld.`;
}

/**
 * Mirrors the API's `z.string().uuid()` on the `betId` path parameter.
 *
 * Same reasoning as `validateFixtureId` above, and the same ONE exception to the
 * rule that the console never re-states a server validation: a bet id reaches
 * the settle panel by being copied out of a support ticket or a log line, so a
 * transposed character is the likely failure and a 404 on a mistyped id reads as
 * "that bet is gone" rather than as "you typed it wrong". Every OTHER id on
 * these screens is carried from a row that was clicked, and is never checked.
 */
export const BET_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateBetId(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'A bet id is required';
  if (!BET_ID_RE.test(trimmed)) {
    return 'A bet id is a UUID — 8-4-4-4-12 hexadecimal characters';
  }
  return null;
}

/* ---------------------------------------------------------------------------
   Odds engine — diagnostics and the one write on that screen
   ---------------------------------------------------------------------------

   NONE of this is on the money path. `validateMarketForPlacement` re-derives
   every acceptance condition from the quote row it has locked and consults no
   diagnostic. So the worst a wrong number here can do is mislead an operator —
   which is exactly why the copy below refuses to round a suspicion up into a
   fault, and why the cache write states its cost rather than its convenience.
*/

/** A live adapter probe, reduced to what the screen actually branches on. */
export interface AdapterProbe {
  ok: boolean;
  lastError?: string | null;
}

/**
 * How many adapters are answering.
 *
 * `down` is counted rather than a bare `allOk` boolean because one adapter down
 * out of four is an availability event and four out of four is an outage, and a
 * single flag makes those read identically.
 */
export function summariseAdapterHealth(probes: readonly AdapterProbe[]): {
  total: number;
  down: number;
  allOk: boolean;
} {
  const down = probes.filter((p) => !p.ok).length;
  return { total: probes.length, down, allOk: probes.length > 0 && down === 0 };
}

/**
 * Cache hit rate, or `null` when nothing has been read.
 *
 * Null rather than 0: a cache nobody has asked anything of has no hit rate, and
 * rendering that as "0%" invites an operator to invalidate a cache that is not
 * the problem.
 */
export function cacheHitRate(stats: { hits: number; misses: number }): number | null {
  const reads = stats.hits + stats.misses;
  if (reads <= 0) return null;
  return stats.hits / reads;
}

/**
 * The restatement on the invalidate panel.
 *
 * It quotes the COST, not the effect: dropping the caches is trivially safe for
 * correctness and expensive for rate-limit budget, and an operator pressing it
 * to "refresh the screen" is the realistic misuse.
 */
export function describeCacheInvalidation(quoteEntries: number, resultEntries: number): string {
  const total = quoteEntries + resultEntries;
  return (
    `Drop all ${total} cached ${total === 1 ? 'entry' : 'entries'} ` +
    `(${quoteEntries} quote, ${resultEntries} result). ` +
    'The next fan-out re-fetches from EVERY provider and spends rate-limit budget, ' +
    'so this is not a refresh button. Prices already struck are unaffected — ' +
    'placement reads the stored quote row, never this cache.'
  );
}

/**
 * The alias table's own structural self-check.
 *
 * An empty list is reported as a sentence, not as blank space, for the reason
 * the quarantine exposure line is: a clean check and a check nobody ran must not
 * look the same.
 */
export function describeAliasTable(problems: readonly string[]): string {
  if (problems.length === 0) return 'No structural problems in the market alias table.';
  return `${problems.length} structural problem${problems.length === 1 ? '' : 's'} in the market alias table — a market aliased wrongly is quoted under a key nothing can settle.`;
}

/**
 * A market only one provider offers.
 *
 * Deliberately worded as SUSPICION. A market genuinely offered by one provider
 * is not a fault, and phrasing it as one trains operators to ignore the list.
 */
export function describeMarketGap(marketKey: string, missingProviders: readonly string[]): string {
  if (missingProviders.length === 0) return `${marketKey} — quoted by every provider.`;
  return `${marketKey} — not offered by ${missingProviders.join(', ')}. This may be correct: a niche market can legitimately have one source. It is only a fault if consensus was expected here.`;
}
